import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateChargingPointConfigurationMutation,
  useDeleteChargingPointMutation,
  useUpdateChargingPointConfigurationMutation,
  useUpdateChargingPointMutation,
  useCreateAdminChargingPointMutation,
  useDeleteAdminChargingPointMutation
} from '../../../redux/api/chargingPoints';

import {
  useCreateChargePointPriceMutation,
  useUpdateChargePointPriceMutation
} from '../../../redux/api/chargePointPriece';

import LoadingAnimation from '../../Utils/BateryChargeAnimation';
import Button from '../../Utils/Button';
import inputTypes from '../../Utils/Form/FormInput/inputTypes';
import IfRender from '../../Utils/IfRender';
import ModalFrame from '../ModalFrame';
import {
  cpIdValidator,
  latitudeValidator,
  longitudeValidator,
  preventNonPrice
} from '../../../Utils';
import XFormInput from '../../Utils/Form/XFormInput/index.jsx';
import XFormSelect from '../../Utils/Form/XFormSelect/index.jsx';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectUserType } from '../../../redux/selectors/userType';
import './style.scss';
import {
  useCreateGeoLocationMutation,
  useUpdateGeoLocationMutation
} from '../../../redux/api/geoLocation.js';

export default function ChargePointModal({
  modalData = {},
  toggleModal = () => {},
  organizations = [],
  refetch,
  cacheParams = {
    page: 1,
    itemsPerPage: 10
  }
}) {
  const { t } = useTranslation();
  const { isAdmin, isSuperAdmin } = useSelector(selectUserType);
  const isEditMode = Object.keys(modalData).length > 0;

  // Form setup
  const initialValues = {
    id: modalData?.id || null,
    slug: modalData?.slug || '',
    chargePointName: modalData?.name || '',
    organizationId: modalData?.organizationId?.toString() || '',
    price: modalData?.prices?.length
      ? parseFloat(modalData?.prices[modalData?.prices?.length - 1]?.price) / 100
      : null,
    priceId: modalData?.prices?.length
      ? modalData?.prices[modalData?.prices?.length - 1]?.id
      : null,
    currency: modalData?.chargePointConfiguration?.currency || 'EUR',
    kwhToAuth: modalData?.chargePointConfiguration?.wattsAuthorize
      ? modalData?.chargePointConfiguration?.wattsAuthorize / 1000
      : null,
    maxPower: modalData?.chargePointConfiguration?.maxPower
      ? modalData?.chargePointConfiguration?.maxPower / 1000
      : null,
    powerType: modalData?.chargePointConfiguration?.powerType || 'AC',
    public: modalData?.public || false,
    latitude: +modalData?.geoLocationPoint?.latitude || '',
    longitude: +modalData?.geoLocationPoint?.longitude || '',
    geoLocationPointId: modalData?.geoLocationPoint?.id || null
  };
  const form = useForm({
    defaultValues: initialValues,
    mode: 'onChange'
  });

  const { handleSubmit, register, clearErrors, setValue, watch } = form;

  const [
    updateChargePoint,
    { isLoading: isEditLoading, isSuccess: isEditSuccess, isError: isEditError, error: editError }
  ] = useUpdateChargingPointMutation();

  const [generalError, setGeneralError] = useState('');
  const [hasBillingAddress, setHasBillingAddress] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currencyCodes] = useState(JSON.parse(import.meta.env.VITE_CURRENCY_CODES));
  const [powerTypes] = useState(['DC', 'AC']);

  const formValues = watch();
  const { maxPower, price, organizationId, public: isPublic, slug, chargePointName } = formValues;

  useEffect(() => {
    const isValid =
      !!slug && !!chargePointName && !!organizationId && !!maxPower && parseFloat(maxPower) > 0;

    setCanSubmit(isValid);
  }, [slug, chargePointName, organizationId, maxPower]);

  const [
    createAdminChargingPoint,
    {
      isLoading: isCreateLoading,
      isSuccess: isCreateSuccess,
      isError: isCreateError,
      error: createError
    }
  ] = useCreateAdminChargingPointMutation();
  const [createGeoLocation] = useCreateGeoLocationMutation();
  const [updateGeoLocation] = useUpdateGeoLocationMutation();
  const [createChargePointPriece] = useCreateChargePointPriceMutation();
  const [createChargingPointConfiguration] = useCreateChargingPointConfigurationMutation();
  const [updateChargingPointConfiguration] = useUpdateChargingPointConfigurationMutation();

  const [
    deleteChargingPoint,
    {
      isLoading: isDeleteLoading,
      isSuccess: isDeleteSuccess,
      isError: isDeleteError,
      error: deleteError
    }
  ] = useDeleteChargingPointMutation();
  const [deleteAdminChargingPoint] = useDeleteAdminChargingPointMutation();

  const powerTypeOptions = powerTypes.map((item) => ({ value: item, label: item }));
  const currencyOptions = currencyCodes.map((item) => ({ value: item, label: item }));
  const organizationOptions = organizations.map((item) => ({
    value: item.id.toString(),
    label: item.name
  }));

  function checkBillingAddress(org) {
    if (!org?.organizationProperties?.length) {
      handleNoBillingAddress();
      return;
    }
    const hasBillingAddressProperty = org.organizationProperties.some(
      (prop) => prop.propertyName === 'client_address'
    );
    const hasContactEmail = org.organizationProperties.some(
      (prop) => prop.propertyName === 'contact_email'
    );
    const hasContactPhone = org.organizationProperties.some(
      (prop) => prop.propertyName === 'contact_phone'
    );

    if (hasBillingAddressProperty && hasContactEmail && hasContactPhone) {
      setHasBillingAddress(true);
    } else {
      handleNoBillingAddress();
    }
  }

  function handleNoBillingAddress() {
    setHasBillingAddress(false);
    setValue('price', '');
    setValue('currency', '');
    setValue('kwhToAuth', '');
    clearErrors(['price', 'currency', 'kwhToAuth']);
  }

  useEffect(() => {
    const org = initialValues.organizationId
      ? organizations.find((org) => org.id === +initialValues.organizationId)
      : organizations[0];
    if (org) {
      checkBillingAddress(org);

      form.setValue('organizationId', org.id.toString());
    }

    if (initialValues.currency) {
      setValue('currency', initialValues.currency);
    }
    if (initialValues.powerType) {
      setValue('powerType', initialValues.powerType);
    } else {
      setValue('powerType', powerTypes[0]);
    }
  }, []);

  useEffect(() => {
    if (organizationId) {
      const org = organizations.find((org) => org.id === +organizationId);
      if (org) {
        checkBillingAddress(org);
      } else {
        handleNoBillingAddress();
      }
    }
  }, [organizationId]);

  useEffect(() => {
    if (isEditSuccess || isDeleteSuccess) {
      toggleModal();
      return;
    }
    const errorMessage =
      editError?.data?.error?.message ||
      createError?.data?.error?.message ||
      deleteError?.data?.error?.message;
    if (isEditError || isCreateError || isDeleteError) {
      setGeneralError(errorMessage);
    }
  }, [
    isEditSuccess,
    isDeleteSuccess,
    isEditError,
    isCreateError,
    isDeleteError,
    editError,
    createError,
    deleteError
  ]);

  const onSubmit = async (values) => {
    try {
      setIsLoading(true);

      const chargePointData = {
        name: values.chargePointName,
        slug: values.slug,
        organizationId: +values.organizationId,
        public: values.public,
        status: 'AVAILABLE'
      };
      const chargePointConfiguration = {
        powerType: values.powerType,
        maxPower: +values.maxPower * 1000,
        currency: hasBillingAddress && values.currency ? values.currency : 'RON',
        wattsAuthorize: hasBillingAddress && values.kwhToAuth ? +values.kwhToAuth * 1000 : 0
      };
      const coordinates = {
        latitude: +values.latitude,
        longitude: +values.longitude
      };
      const initialCoordinates = {
        latitude: +initialValues?.latitude,
        longitude: +initialValues?.longitude
      };
      if (values.latitude && values.longitude) {
        const shouldUpdateGeoLocation =
          initialCoordinates.latitude !== coordinates.latitude ||
          initialCoordinates.longitude !== coordinates.longitude;

        if (shouldUpdateGeoLocation && isEditMode) {
          await updateGeoLocation({ id: initialValues?.geoLocationPointId, data: coordinates });
        }
        if (!initialValues?.geoLocationPointId && !isEditMode) {
          const response = await createGeoLocation(coordinates);
          if (response.data?.id) {
            chargePointData.geoLocationPointId = response.data.id;
          }
        }
      }

      if (isEditMode) {
        const patchData = {
          ...chargePointData,
          geoLocationPointId: chargePointData.geoLocationPointId || modalData.geoLocationPointId
        };

        try {
          await updateChargePoint({
            id: modalData.id,
            cacheParams,
            data: patchData
          });

          if (hasBillingAddress) {
            const priceData = { price: values.price * 100 };
            if (
              !initialValues.priceId ||
              (initialValues.priceId && priceData.price !== initialValues.price * 100)
            ) {
              await createChargePointPriece({
                id: modalData.id,
                data: priceData
              });
            }
          }
          if (modalData.chargePointConfiguration) {
            await updateChargingPointConfiguration({
              data: chargePointConfiguration,
              id: modalData.id
            });
          } else {
            await createChargingPointConfiguration({
              data: chargePointConfiguration,
              id: modalData.id
            });
          }
        } catch (error) {
          handleSubmitError(error);
        }
      } else {
        try {
          const chargePoint = await createAdminChargingPoint({
            postData: chargePointData,
            orgId: chargePointData.organizationId
          });
          const chargePointId = chargePoint.data.id;
          if (hasBillingAddress) {
            await createChargePointPriece({
              id: chargePointId,
              data: { price: values.price * 100 }
            });
          }
          await createChargingPointConfiguration({
            data: chargePointConfiguration,
            id: chargePointId
          });
        } catch (error) {
          handleSubmitError(error);
        }
      }
    } catch (error) {
      handleSubmitError(error);
    } finally {
      refetch();
      setIsLoading(false);
    }
  };

  const handleSubmitError = (error) => {
    setIsLoading(false);
    const message =
      error?.data?.error?.message ?? 'An error occurred while saving the charging point';
    setGeneralError(message);
  };

  const onDelete = async (id) => {
    if (
      confirm(t('charger_deletion_first_confirmation')) &&
      confirm(t('charger_deletion_second_confirmation')) &&
      confirm(t('charger_deletion_third_confirmation'))
    ) {
      try {
        if (isSuperAdmin) {
          const res = await deleteChargingPoint(id);
        } else if (isAdmin) {
          await deleteAdminChargingPoint(modalData.organizationId, id);
        }
      } catch (error) {
        setGeneralError(error?.data?.error?.message);
      }
    }
  };

  return (
    <ModalFrame
      onClose={toggleModal}
      bodyClass='modal__body--charge-point'
      closeOnBgClick={!isEditLoading && !isDeleteLoading}>
      <IfRender
        condition={!isEditLoading && !isDeleteLoading}
        otherwise={<LoadingAnimation dark />}>
        <FormProvider {...form}>
          <form id='edit-user' className='form' onSubmit={handleSubmit(onSubmit)}>
            <div className='form__section-title modal-form__header'>
              <h1>{isEditMode ? t('edit_charging_point') : t('add_charging_point')}</h1>
              <Button
                className='button__icon color--alfa-foreground'
                iconName='close'
                onClick={toggleModal}
              />
            </div>

            <div className='flex-inline'>
              <XFormInput
                id='slug'
                name='slug'
                type={inputTypes.text}
                label={t('charge_point_id')}
                placeholder={t('add_charge_point_id_here')}
                ref={register('slug', {
                  required: 'error_field_required',
                  validate: cpIdValidator
                })}
              />

              <XFormInput
                id='chargePointName'
                name='chargePointName'
                type={inputTypes.text}
                label={t('charge_point_name')}
                placeholder={t('add_charge_point_name_here')}
                ref={register('chargePointName', {
                  required: 'error_field_required',
                  validate: cpIdValidator
                })}
              />

              <div className='price_wrapper'>
                <XFormInput
                  id='price'
                  name='price'
                  step={0.01}
                  type={inputTypes.number}
                  onBeforeInput={preventNonPrice}
                  disabled={!hasBillingAddress}
                  className={!hasBillingAddress}
                  label={t('price')}
                  ref={register('price', {
                    required: false
                  })}
                />
                {!hasBillingAddress && (
                  <p className='price_wrapper__warning_text'>{t('price_disabled')}</p>
                )}
              </div>

              <XFormSelect
                wrapClass='input__wrap'
                id='currency'
                name='currency'
                label={t('currency')}
                options={currencyOptions}
                blankOptionName={t('select_currency')}
                disabled={!hasBillingAddress}
                className={!hasBillingAddress}
                ref={register('currency', {
                  required: !!(hasBillingAddress && price)
                })}
              />

              <XFormInput
                id='kwhToAuth'
                name='kwhToAuth'
                type={inputTypes.number}
                label={t('kwh_to_authorize')}
                step={0.01}
                min={0.1}
                disabled={!hasBillingAddress}
                className={!hasBillingAddress}
                ref={register('kwhToAuth')}
              />

              <XFormInput
                id='maxPower'
                name='maxPower'
                type={inputTypes.number}
                label={t('max_power')}
                step={0.01}
                ref={register('maxPower', {
                  required: 'error_field_required'
                })}
              />

              <XFormSelect
                wrapClass='input__wrap'
                id='powerType'
                name='powerType'
                label={t('power_type')}
                blankOptionName={t('select_power_type')}
                options={powerTypeOptions}
                ref={register('powerType', {
                  required: 'error_field_required'
                })}
              />

              <XFormInput
                id='longitude'
                name='longitude'
                type={inputTypes.number}
                step='any'
                label={t('longitude')}
                ref={register('longitude', { validate: longitudeValidator })}
              />

              <XFormInput
                id='latitude'
                name='latitude'
                step='any'
                type={inputTypes.number}
                label={t('latitude')}
                ref={register('latitude', { validate: latitudeValidator })}
              />

              <XFormSelect
                wrapClass='input__wrap'
                id='organizationId'
                name='organizationId'
                placeholder='Select Organization'
                label={t('organization')}
                blankOptionName={t('select_an_organization')}
                options={organizationOptions}
                ref={register('organizationId', { required: 'error_field_required' })}
              />

              <XFormInput
                id='public'
                name='public'
                wrapClass='public__wrap'
                labelClass='public__label'
                inputClass='public__checkbox'
                label={t('public_charging_point')}
                type={inputTypes.checkbox}
                onChange={(e) => {
                  setValue('public', e.target.checked);
                }}
                checked={isPublic}
              />
            </div>

            <div className='form__section-footer'>
              {generalError && <p className='input__label error'>{generalError}</p>}

              {isEditMode && (
                <Button
                  className='button__bravo'
                  label={t('delete')}
                  onClick={() => onDelete(modalData?.id)}
                />
              )}

              <Button
                className='button__alfa'
                label={t('save')}
                disabled={!canSubmit || isLoading}
                type='submit'
              />
            </div>
          </form>
        </FormProvider>
      </IfRender>
    </ModalFrame>
  );
}

ChargePointModal.propTypes = {
  modalData: PropTypes.object,
  toggleModal: PropTypes.func,
  organizations: PropTypes.array,
  cacheParams: PropTypes.object
};
